import React, { useEffect, useRef, useState } from 'react'

import { useGiftOrdersList } from './hooks'
import { FilterValue } from 'antd/es/table/interface'
import { Badge, Button, Input, InputRef, Space, Table, Tag } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import Highlighter from 'react-highlight-words'
import { ColumnsType } from 'antd/lib/table'
import { CButton, CCardBody } from '@coreui/react-pro'
import { path } from '../../../../libs'
import CIcon from '@coreui/icons-react'
import { cilPencil, cilZoom } from '@coreui/icons'
import { DataIndex, GiftOrder } from './types'
import {
  colorGiftOrderStatuses,
  EGiftOrderStatuses,
  giftOrderFilterStatuses,
} from '../const'
import { Guard } from '../../../../features/session/guard'
import { ReviewTaskModal } from '../atoms/reviewTaskModal/component'
import { submitGiftOrderStatus } from '../details/model'

export const GiftOrdersList = () => {
  const {
    giftOrders,
    totalCountOfGiftOrders,
    onChangeGiftOrdersList,
    loading,
  } = useGiftOrdersList()

  const [searchText, setSearchText] = useState<{
    [key: string]: string
  }>({})
  const [orderBy, setOrderBy] = useState('id')
  const [orderDirection, setOrderDirection] = useState('-')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [filtersState, setFiltersState] = useState<
    Record<string, FilterValue | null>
  >({})
  const [refreshTick, setRefreshTick] = useState(0)
  const [reviewTask, setReviewTask] = useState<{ data: GiftOrder } | null>(
    null,
  )

  const searchInput = useRef<InputRef>(null)

  useEffect(() => {
    const unsub = submitGiftOrderStatus.done.watch(() => {
      setRefreshTick((tick) => tick + 1)
    })
    return () => unsub()
  }, [])

  const handleTableChange = ({
    currentPage,
    currentPageSize,
    currentOrderBy,
    currentOrderDirection,
    filters,
  }: {
    currentPage?: number
    currentPageSize?: number
    currentOrderBy?: string
    currentOrderDirection?: string
    filters?: Record<string, FilterValue | null>
  }) => {
    if (currentPage) {
      setPage(currentPage)
    }
    if (currentPageSize) {
      setPageSize(currentPageSize)
    }
    if (currentOrderBy) {
      setOrderBy(currentOrderBy)
    }
    if (currentOrderDirection) {
      if (currentOrderDirection == 'descend') {
        setOrderDirection('-')
      } else {
        setOrderDirection('')
      }
    }

    if (filters) {
      setFiltersState(filters)
    }
  }

  const handleReset = (clearFilters: () => void, dataIndex: DataIndex) => {
    clearFilters()
    setSearchText((prev) => {
      delete prev[dataIndex]
      return {
        ...prev,
      }
    })
  }

  useEffect(() => {
    onChangeGiftOrdersList({
      limit: pageSize,
      page: page,
      orderBy: orderBy,
      orderDirection: orderDirection,
      searchText: searchText,
      filters: {
        status: filtersState?.status && (filtersState.status as string[]),
      },
    })
  }, [
    pageSize,
    page,
    orderBy,
    orderDirection,
    searchText,
    filtersState,
    refreshTick,
  ])

  const getColumnSearchProps = (
    dataIndex: DataIndex,
  ): {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }: {
      setSelectedKeys: any
      selectedKeys: any
      confirm: any
      clearFilters?: any
      close: any
    }) => JSX.Element
    filterIcon: (filtered: boolean) => JSX.Element
    onFilter: (value: any, record: any) => boolean
    render: (text: any) => any
    onFilterDropdownOpenChange: (visible: any) => void
  } => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      clearFilters,
      close,
    }) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() =>
            setSearchText((prev) => ({ ...prev, [dataIndex]: selectedKeys[0] }))
          }
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() =>
              setSearchText((prev) => ({
                ...prev,
                [dataIndex]: selectedKeys[0],
              }))
            }
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, dataIndex)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              close()
            }}
          >
            close
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        .toString()
        .toLowerCase()
        .includes((value as string).toLowerCase()),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100)
      }
    },
    render: (text) =>
      searchText[dataIndex] ? (
        <Highlighter
          highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
          searchWords={[searchText[dataIndex]]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  })

  const columns: ColumnsType<GiftOrder> = [
    {
      title: 'ID',
      dataIndex: 'id',
      ...getColumnSearchProps('id'),
      sorter: true,
      defaultSortOrder: 'descend',
    },
    {
      title: 'Gift',
      dataIndex: 'gift',
      ...getColumnSearchProps('gift.name'),
      render: (item) => item.name,
    },
    {
      title: 'Created at',
      dataIndex: 'createdAt',
      render: (createdAt: string, record: GiftOrder) => record?.humanCreatedAt,
    },
    {
      title: 'Client',
      dataIndex: 'client',
      ...getColumnSearchProps('client.tsname'),
      render: (item) => (
        <div>
          {item?.isTest ? (
            <Badge count={'Test'} offset={[0, -10]}>
              <a
                href={path.clients.details(String(item.clientId))}
                target="_blank"
              >
                {item.fullName}
              </a>
            </Badge>
          ) : (
            <a
              href={path.clients.details(String(item.clientId))}
              target="_blank"
            >
              {item.fullName}
            </a>
          )}
        </div>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'client',
      key: 'email',
      ...getColumnSearchProps('client.email' as any),
      render: (item) => (
        <span style={{ fontSize: 11, fontFamily: 'monospace' }}>{item?.email}</span>
      ),
    },
    {
      title: 'Crown',
      dataIndex: 'giftPrice',
      ...getColumnSearchProps('giftPrice'),
      render: (giftPrice: number) => giftPrice,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      filterSearch: true,
      render: (item: string) => (
        <Tag color={colorGiftOrderStatuses(item)}>
          {EGiftOrderStatuses[item as keyof typeof EGiftOrderStatuses]}
        </Tag>
      ),
      filters: giftOrderFilterStatuses,
      onFilter: (value: any, record) => record.status == value,
    },
    {
      title: 'Gift cost price',
      dataIndex: 'gift',
      render: (gift) => gift?.costPrice ?? '—',
    },
    {
      title: 'Cost price',
      dataIndex: 'costPrice',
      ...getColumnSearchProps('costPrice'),
      sorter: true,
      render: (item, record) => {
        if (item > 0) {
          return item
        }

        return record.gift.costPrice
      },
    },
    {
      title: 'Shipping Cost',
      dataIndex: 'shippingPrice',
      ...getColumnSearchProps('shippingPrice'),
      sorter: true,
    },
    {
      title: 'Actions',
      fixed: 'right',
      width: 100,
      render: (item: GiftOrder) => {
        const isQuickReviewEligible =
          item.status === EGiftOrderStatuses.NEW &&
          item.gift?.giftType === 'GIFT_TYPE_BONUS_CROWNS_REVIEW_SCREENSHOT'

        return (
          <>
            <Guard permission="view_gift_order">
              {isQuickReviewEligible ? (
                <CButton
                  color="secondary"
                  className="me-2"
                  title="Review"
                  onClick={() => setReviewTask({ data: item })}
                >
                  <CIcon icon={cilZoom} />
                </CButton>
              ) : (
                <CButton
                  color="secondary"
                  className="me-2"
                  title="View"
                  href={path.giftShop.giftOrders.details(String(item.id))}
                >
                  <CIcon icon={cilZoom} />
                </CButton>
              )}
            </Guard>
            <Guard permission="edit_gift_order">
              <CButton
                color="secondary"
                className="me-2"
                title="Edit"
                href={path.giftShop.giftOrders.edit(String(item.id))}
              >
                <CIcon icon={cilPencil} />
              </CButton>
            </Guard>
          </>
        )
      },
    },
  ]

  return (
    <CCardBody>
      <Table
        dataSource={giftOrders}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="giftOrderId"
        columns={columns}
        pagination={{
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [15, 30, 50, 100],
          total: totalCountOfGiftOrders,
          position: ['topRight', 'bottomRight'],
        }}
        onChange={(pagination, filters, sorter, extra) =>
          handleTableChange({
            //@ts-ignore
            currentPage: pagination.current, //@ts-ignore
            currentPageSize: pagination.pageSize, //@ts-ignore
            currentOrderDirection: sorter.order, //@ts-ignore
            currentOrderBy: sorter.field,
            filters,
          })
        }
      ></Table>
      {reviewTask && (
        <ReviewTaskModal
          isVisible={Boolean(reviewTask)}
          onClose={() => setReviewTask(null)}
          data={reviewTask.data}
        />
      )}
    </CCardBody>
  )
}
